import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const OralVAEPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const audioRef = useRef(null);

  const slides = [
    {
      type: 'title',
      title: "L'oral VAE",
      subtitle: "La présentation personnelle",
      description: "Préparez-vous à réussir votre oral devant le jury"
    },
    {
      type: 'motivation',
      title: "Avant de commencer...",
      content: [
        { text: "Oui, un oral VAE, c'est stressant. Et c'est normal.", highlight: false },
        { text: "Mais c'est une étape INDISPENSABLE pour concrétiser votre projet professionnel.", highlight: true },
        { text: "Si vous êtes ici aujourd'hui, c'est que vous avez envie d'avancer, d'évoluer et de faire reconnaître votre parcours.", highlight: false },
        { text: "Et d'être enfin RECONNU(E)", highlight: true }
      ],
      note: "Cette formation est là pour vous y aider. Pour celles et ceux qui le souhaitent, nous pourrons également travailler ensemble sur la gestion du stress."
    },
    {
      type: 'sommaire',
      title: "Sommaire",
      items: [
        "Introduction",
        "Objectif de la VAE",
        "Parcours professionnel",
        "Formations & compétences",
        "Qualités personnelles",
        "Projet professionnel",
        "Conclusion"
      ]
    },
    {
      type: 'content',
      title: "Les premières minutes sont déterminantes",
      points: [
        { icon: "👋", title: "Premier contact réel avec le jury", text: "Avant l'oral, le jury ne vous connaît qu'à travers votre Livret 2." },
        { icon: "🎯", title: "Poser le cadre", text: "L'introduction permet de poser le cadre de l'échange et d'installer une relation de confiance." },
        { icon: "⭐", title: "Faire bonne impression", text: "Les premières minutes sont essentielles car elles constituent le premier contact réel." }
      ]
    },
    {
      type: 'timing',
      title: "Combien de temps dure la présentation ?",
      duration: "5 à 10 minutes",
      notes: [
        "Le jury peut choisir de passer plus vite aux questions",
        "Cela ne remet pas en cause la qualité de votre présentation",
        "Un jury pressé ≠ une mauvaise présentation"
      ]
    },
    {
      type: 'checklist',
      title: "Par quoi commencer ?",
      subtitle: "Par le commencement : VOUS",
      items: [
        { text: "Se présenter : nom et prénom", required: true },
        { text: "L'âge (facultatif)", required: false },
        { text: "La situation personnelle (uniquement si vous le souhaitez)", required: false },
        { text: "Mon parcours scolaire", required: true },
        { text: "Mon parcours professionnel", required: true }
      ],
      note: "L'introduction commence toujours par une présentation simple. L'âge et la situation personnelle ne sont jamais obligatoires."
    },
    {
      type: 'twoColumns',
      title: "Et ensuite... que dire au jury ?",
      column1: {
        number: "1",
        title: "Présenter son contexte professionnel",
        items: [
          "Le lieu principal choisi pour le Livret 2",
          "Le type de structure",
          "Sa place et son rôle dans l'équipe"
        ]
      },
      column2: {
        number: "2",
        title: "Parler de ses missions",
        items: [
          "Missions en lien avec le référentiel du diplôme",
          "Présentation globale (sans raconter les situations du Livret 2)"
        ]
      },
      tip: "L'objectif n'est pas de réciter votre Livret 2. Le jury le connaît déjà. Montrez que vous comprenez votre rôle professionnel et vos compétences."
    },
    {
      type: 'highlight',
      title: "Pourquoi mettre en avant mes formations ?",
      mainPoint: "Mettre en avant mes formations montre mon implication dans une démarche de veille professionnelle.",
      benefits: [
        "Démontre que je suis investie dans mon projet d'évolution professionnelle",
        "Montre que je suis actrice de mon parcours, et non dans une posture passive"
      ]
    },
    {
      type: 'threeCards',
      title: "Pourquoi ce diplôme est important pour moi ?",
      cards: [
        {
          icon: "🏆",
          title: "Une reconnaissance professionnelle",
          items: [
            "Faire reconnaître officiellement mes compétences",
            "Donner une légitimité à mon parcours"
          ],
          color: "#667eea"
        },
        {
          icon: "📈",
          title: "Une évolution professionnelle",
          items: [
            "Évoluer dans mes missions",
            "Accéder à de nouvelles responsabilités",
            "Sécuriser mon parcours"
          ],
          color: "#10B981"
        },
        {
          icon: "💪",
          title: "Un aboutissement personnel",
          items: [
            "Valoriser des années d'engagement",
            "Renforcer la confiance en moi",
            "Donner du sens à mon parcours"
          ],
          color: "#E75480"
        }
      ]
    },
    {
      type: 'conclusion',
      title: "Comment bien terminer sa présentation ?",
      subtitle: "Éviter le blanc de fin",
      tip: "Il est important d'annoncer clairement la fin de sa présentation.",
      benefits: [
        "Tu connais le déroulé de l'oral",
        "Tu as préparé ce moment d'échange",
        "Tu es à l'aise et structuré(e)"
      ],
      example: "« J'ai terminé ma présentation personnelle. Je vous remercie pour votre écoute et je suis à votre disposition pour répondre à vos questions. »"
    },
    {
      type: 'summary',
      title: "En résumé - L'essentiel à retenir",
      checklist: [
        "Je me présente",
        "Je parle de mon parcours professionnel",
        "Je présente mon lieu de travail (structure, public, rôle)",
        "Je présente mes missions principales",
        "Je parle de mes formations et de ma veille professionnelle",
        "J'explique ce que la VAE va m'apporter",
        "Je conclus clairement"
      ],
      warning: "Attention : utiliser le « JE » tout au long de l'oral. C'est vous qui vous présentez, pas l'équipe."
    },
    {
      type: 'end',
      title: "Merci de votre attention",
      subtitle: "Cette présentation est terminée.",
      message: "Je reste disponible pour vos questions.",
      note: "Pas d'inquiétude, le jury ne mange personne ;)"
    }
  ];

  const speakText = async (text, isAutoPlay = false) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (isPlaying && !isAutoPlay) {
      setIsPlaying(false);
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      if (!isAutoPlay) {
        toast.info('Génération de l\'audio en cours...');
      }

      const response = await fetch(`${BACKEND_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'nova' })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      
      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
        setIsLoading(false);
        audio.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      if (!isAutoPlay) {
        toast.error('Erreur lors de la génération audio');
      }
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  // Auto-play TTS when slide changes
  useEffect(() => {
    if (autoPlay) {
      const slide = slides[currentSlide];
      const text = getSlideText(slide);
      // Small delay to let the slide render first
      const timer = setTimeout(() => {
        speakText(text, true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, autoPlay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
  };

  const toggleAutoPlay = () => {
    if (autoPlay) {
      stopAudio();
    }
    setAutoPlay(!autoPlay);
    toast.info(autoPlay ? 'Lecture automatique désactivée' : 'Lecture automatique activée');
  };

  const getSlideText = (slide) => {
    let text = slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle + '. ';
    if (slide.content) text += slide.content.map(c => c.text).join(' ');
    if (slide.items) text += slide.items.join('. ');
    if (slide.points) text += slide.points.map(p => p.title + ': ' + p.text).join('. ');
    if (slide.notes) text += slide.notes.join('. ');
    if (slide.checklist) text += slide.checklist.join('. ');
    if (slide.note) text += slide.note;
    if (slide.tip) text += slide.tip;
    if (slide.example) text += slide.example;
    if (slide.message) text += slide.message;
    return text;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      stopAudio();
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      stopAudio();
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    stopAudio();
    setCurrentSlide(index);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#667eea', marginBottom: '1rem' }}>
              {slide.title}
            </h1>
            <h2 style={{ fontSize: '1.5rem', color: '#E75480', marginBottom: '2rem' }}>
              {slide.subtitle}
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>{slide.description}</p>
          </div>
        );

      case 'motivation':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              {slide.content.map((item, i) => (
                <p key={i} style={{
                  fontSize: item.highlight ? '1.25rem' : '1.1rem',
                  fontWeight: item.highlight ? '600' : '400',
                  color: item.highlight ? '#667eea' : '#444',
                  padding: item.highlight ? '1rem' : '0',
                  background: item.highlight ? '#667eea10' : 'transparent',
                  borderRadius: item.highlight ? '0.5rem' : '0',
                  borderLeft: item.highlight ? '4px solid #667eea' : 'none'
                }}>
                  {item.text}
                </p>
              ))}
            </div>
            <p style={{ fontSize: '0.95rem', color: '#666', fontStyle: 'italic', background: '#f9f9f9', padding: '1rem', borderRadius: '0.5rem' }}>
              {slide.note}
            </p>
          </div>
        );

      case 'sommaire':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem', textAlign: 'center' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '400px', margin: '0 auto' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  background: 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)',
                  borderRadius: '0.5rem',
                  borderLeft: '3px solid #667eea'
                }}>
                  <span style={{ 
                    background: '#667eea',
                    color: 'white',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>{i + 1}</span>
                  <span style={{ fontSize: '1rem' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'content':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {slide.points.map((point, i) => (
                <div key={i} style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.25rem',
                  background: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ fontSize: '2rem' }}>{point.icon}</span>
                  <div>
                    <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>{point.title}</h4>
                    <p style={{ color: '#666', fontSize: '0.95rem' }}>{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'timing':
        return (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              marginBottom: '2rem',
              display: 'inline-block'
            }}>
              <span style={{ fontSize: '1rem' }}>⏱️ En général :</span>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{slide.duration}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left', maxWidth: '500px', margin: '0 auto' }}>
              {slide.notes.map((note, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: '#f9f9f9',
                  borderRadius: '0.5rem'
                }}>
                  <span style={{ color: '#10B981' }}>✓</span>
                  <span style={{ fontSize: '0.95rem' }}>{note}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'checklist':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>
              {slide.title}
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#E75480', marginBottom: '2rem' }}>{slide.subtitle}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: item.required ? '#667eea10' : '#f9f9f9',
                  borderRadius: '0.5rem',
                  borderLeft: item.required ? '3px solid #667eea' : '3px solid #ddd'
                }}>
                  <span style={{ color: item.required ? '#667eea' : '#999' }}>
                    {item.required ? '✓' : '○'}
                  </span>
                  <span style={{ fontSize: '0.95rem', color: item.required ? '#333' : '#666' }}>{item.text}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic', background: '#fffbeb', padding: '1rem', borderRadius: '0.5rem' }}>
              💡 {slide.note}
            </p>
          </div>
        );

      case 'twoColumns':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              {[slide.column1, slide.column2].map((col, i) => (
                <div key={i} style={{
                  background: i === 0 ? '#667eea10' : '#E7548010',
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  borderTop: `4px solid ${i === 0 ? '#667eea' : '#E75480'}`
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <span style={{
                      background: i === 0 ? '#667eea' : '#E75480',
                      color: 'white',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}>{col.number}</span>
                    <h4 style={{ fontWeight: '600', color: '#333' }}>{col.title}</h4>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {col.items.map((item, j) => (
                      <li key={j} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.95rem'
                      }}>
                        <span style={{ color: i === 0 ? '#667eea' : '#E75480' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', background: '#f0f9ff', padding: '1rem', borderRadius: '0.5rem', borderLeft: '3px solid #3B82F6' }}>
              👉 {slide.tip}
            </p>
          </div>
        );

      case 'highlight':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{
              background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
              color: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              marginBottom: '1.5rem',
              fontSize: '1.1rem',
              lineHeight: '1.6'
            }}>
              {slide.mainPoint}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.benefits.map((benefit, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: '#10B98110',
                  borderRadius: '0.5rem'
                }}>
                  <span style={{ color: '#10B981', fontSize: '1.25rem' }}>✓</span>
                  <span style={{ fontSize: '0.95rem' }}>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'threeCards':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem', textAlign: 'center' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {slide.cards.map((card, i) => (
                <div key={i} style={{
                  background: 'white',
                  padding: '1.25rem',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  borderTop: `4px solid ${card.color}`
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{card.icon}</div>
                  <h4 style={{ fontWeight: '600', marginBottom: '0.75rem', color: card.color, fontSize: '1rem' }}>{card.title}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {card.items.map((item, j) => (
                      <li key={j} style={{ fontSize: '0.85rem', marginBottom: '0.4rem', color: '#555' }}>
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'conclusion':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>
              {slide.title}
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#E75480', marginBottom: '2rem' }}>{slide.subtitle}</p>
            <p style={{ fontSize: '1rem', marginBottom: '1.5rem', color: '#444' }}>👉 {slide.tip}</p>
            <p style={{ fontSize: '0.95rem', marginBottom: '1rem' }}>Cela montre que :</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {slide.benefits.map((benefit, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: '#f9f9f9',
                  borderRadius: '0.25rem'
                }}>
                  <span style={{ color: '#10B981' }}>✓</span>
                  <span style={{ fontSize: '0.9rem' }}>{benefit}</span>
                </div>
              ))}
            </div>
            <div style={{
              background: '#667eea',
              color: 'white',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              fontStyle: 'italic',
              fontSize: '0.95rem',
              lineHeight: '1.6'
            }}>
              💬 {slide.example}
            </div>
          </div>
        );

      case 'summary':
        return (
          <div style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#333', marginBottom: '2rem' }}>
              {slide.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
              {slide.checklist.map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.6rem 1rem',
                  background: '#667eea10',
                  borderRadius: '0.5rem'
                }}>
                  <span style={{
                    background: '#667eea',
                    color: 'white',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem'
                  }}>✓</span>
                  <span style={{ fontSize: '0.95rem' }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              padding: '1rem',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>⚠️</span>
              <span style={{ fontSize: '0.9rem', color: '#92400E' }}>{slide.warning}</span>
            </div>
          </div>
        );

      case 'end':
        return (
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#667eea', marginBottom: '1rem' }}>
              {slide.title}
            </h1>
            <p style={{ fontSize: '1.25rem', color: '#666', marginBottom: '1.5rem' }}>{slide.subtitle}</p>
            <p style={{ fontSize: '1.1rem', color: '#444', marginBottom: '2rem' }}>{slide.message}</p>
            <div style={{
              background: '#10B98120',
              padding: '1rem 2rem',
              borderRadius: '2rem',
              display: 'inline-block',
              color: '#10B981',
              fontStyle: 'italic'
            }}>
              {slide.note}
            </div>
          </div>
        );

      default:
        return <div>Slide non définie</div>;
    }
  };

  const slide = slides[currentSlide];

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '1rem 1.5rem',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <h3 style={{ fontWeight: '600' }}>Présentation Oral VAE</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Auto-play toggle */}
          <button
            onClick={toggleAutoPlay}
            style={{
              background: autoPlay ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
              border: autoPlay ? '2px solid white' : '2px solid rgba(255,255,255,0.3)',
              borderRadius: '0.5rem',
              padding: '0.4rem 0.75rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.8rem',
              transition: 'all 0.2s'
            }}
          >
            {autoPlay ? <Volume2 size={16} /> : <VolumeX size={16} />}
            Auto
          </button>
          
          {/* Manual play/stop */}
          <button
            onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))}
            disabled={isLoading}
            style={{
              background: isPlaying ? '#ef4444' : 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.4rem 0.75rem',
              color: 'white',
              cursor: isLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.8rem'
            }}
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
            ) : isPlaying ? (
              <VolumeX size={16} />
            ) : (
              <Volume2 size={16} />
            )}
            {isLoading ? 'Chargement...' : isPlaying ? 'Stop' : 'Écouter'}
          </button>
          
          <span style={{ fontSize: '0.9rem', marginLeft: '0.5rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
      </div>

      {/* Slide Content */}
      <div style={{ minHeight: '450px', background: '#fafafa' }}>
        {renderSlide(slide)}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderTop: '1px solid #e5e7eb',
        background: 'white'
      }}>
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: currentSlide === 0 ? '#f3f4f6' : '#667eea',
            color: currentSlide === 0 ? '#9ca3af' : 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                border: 'none',
                background: i === currentSlide ? '#667eea' : '#e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: currentSlide === slides.length - 1 ? '#f3f4f6' : '#667eea',
            color: currentSlide === slides.length - 1 ? '#9ca3af' : 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: currentSlide === slides.length - 1 ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
    </>
  );
};

export default OralVAEPresentation;
